import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import NoData from 'components/noData/noData';
import ListButton from 'components/listButton/listButton';
import ActionListHeaderComponent from '../../listHeader';
import StatusComponent from 'components/status/status';
import LoadingComponent from 'components/loadingComponent/loadingComponent';

// services

// styles

// models
import { RegistryTransaction, RegistryTransactionListItem } from '../models/registryTransaction';
import { onGetRegistryTransactionsList } from '../services/registryTransactionServices';
import NotificationService from 'core/services/notification';
import TableTotal from 'components/tableTotal/tableTotal';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { ServiceCost } from '../../commonModels';
import { onGetServiceCost } from '../../commonServices';

interface RegistryTransactionsListPageProps {}

const RegistryTransactionsListPage: FunctionComponent<RegistryTransactionsListPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [requests, setRequests] = useState<RegistryTransactionListItem[]>([]);

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<RegistryTransactionListItem>['columns'] = [
		{
			title: t('ACTIONS.REQUEST_CODE'),
			dataIndex: 'code',
			key: 'code',
			render: (code) => <>#{code}</>,
		},
		{
			title: t('ACTIONS.REQUEST_DATE'),
			dataIndex: 'created_at',
			key: 'created_at',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status?.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.SELLER_NATIONAL_ID'),
			dataIndex: 'source',
			key: 'seller_national_id',
			render: (source) => source.national_id,
		},
		{
			title: t('ACTIONS.SELLER_NAME'),
			dataIndex: 'source',
			key: 'seller_name',
			render: (source) => (isRTL() ? source.name : source.name_en ?? source.name),
		},
		{
			title: t('ACTIONS.BUYER_NATIONAL_ID'),
			dataIndex: 'destination',
			key: 'buyer_national_id',
			render: (destination) => destination.national_id,
		},
		{
			title: t('ACTIONS.BUYER_NAME'),
			dataIndex: 'destination',
			key: 'buyer_name',
			render: (destination) => (isRTL() ? destination.name : destination.name_en ?? destination.name),
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => <ListButton type="view" onClick={() => navigate(`${record.id}`)} />,
		},
	];

	useEffect(() => {
		setCurrentPage(getCurrentPage());
		getRegistryTransactionsInfo();
		getServiceCost();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('registry_transaction');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getRegistryTransactionsInfo = async () => {
		try {
			setIsLoading(true);

			const response = await onGetRegistryTransactionsList(getCurrentPage());

			setRequests(response.registry_transaction_actions);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetRegistryTransactionsList(getCurrentPage());
			setRequests(response.registry_transaction_actions);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.REGISTRY_TRANSACTIONS')}
				subTitle={t('ACTIONS.TRANSFER_SHARES_DESC')}
				costLoading={serviceCostLoading}
				costs={[serviceCost!]}
			/>

			<Table
				columns={columns}
				dataSource={requests}
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				className="bordered"
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
				locale={{ emptyText: <NoData /> }}
			/>
		</>
	);
};

export default RegistryTransactionsListPage;
